import React, { useState } from 'react';
import { ApolloProvider } from '@apollo/client';
import { QueryClientProvider } from '@tanstack/react-query';
import FroalaJsEditor from 'froala-editor';
import { queryClient } from '@/utils/ReactQuery';
import AddImage, { IAddImageProps } from '@/components/admin/ImageManager/Add';
import { insertIntoEditor }
  from '@/components/admin/SlideShow/Slide/Content/SlideObjectText/CustomFroalaCommands/FroalaListeners';
import CustomVideoModal
  from '@/components/admin/SlideShow/Slide/Content/SlideObjectText/CustomFroalaCommands/CustomVideo/CustomVideoModal';
import GraphQlImageModel from '@/components/interfaces/GraphQlImageModel';
import EquationInsert
  from '@/components/admin/SlideShow/Slide/Content/SlideObjectText/CustomFroalaCommands/EquationInsert';
import { useScript } from '../../common/Utils';
import { useModalManager } from '../../common/Modal';
import FroalaEditor from '../../common/FroalaEditor';
import apolloClient from '../../common/ApolloClient';
import TextEditorConfig from './FroalaConfig';
import { registerFroalaCommands } from './customCommands';
import EditImageModal, { EditImageModalProps } from './EditImageModal';
import ImageMarginEditor from './ImageMarginEditor';
import { EQUATION_MODEL_TYPES } from './froalaConstants';
import { extendFroalaTemplates, graphQlImageModelToTag } from './froalaUtils';
import { AvailablePlugin, EventHandlerEntry, PluginProps } from './froalaTypes';

extendFroalaTemplates();
registerFroalaCommands();

interface IAddImagePropsDerived
  extends Omit<IAddImageProps, 'afterSubmit' | 'afterMergeDuplicates' | 'dontMergeCallback'> {
  afterSubmit: (imageModel: GraphQlImageModel, editorInstance: typeof FroalaJsEditor) => any;
  afterMergeDuplicates?: (imageModels: GraphQlImageModel[], editorInstance: typeof FroalaJsEditor) => any;
  dontMergeCallback?: (imageModel: GraphQlImageModel, editorInstance: typeof FroalaJsEditor) => any;
}

export interface FroalaProps {
  activePlugins?: AvailablePlugin[];
  className?: string;
  eventHandlers?: EventHandlerEntry[];
  customConfig?: object;
  customEvents?: object;
  modelId?: string | number;
  modelType: string;
  name: string;
  onChange?: (text: string) => any;
  pluginProps?: PluginProps;
  setEditor?: (editor: typeof FroalaJsEditor) => void;
  text: string;
  userType: UserType;
  AddImageProps?: Partial<IAddImagePropsDerived>;
  EditImageProps?: Partial<EditImageModalProps>;
}

const Froala = ({
  activePlugins = [],
  className,
  eventHandlers = [],
  customConfig = {},
  customEvents = {},
  modelId,
  modelType,
  name,
  pluginProps = {},
  setEditor: setEditorCallback,
  text,
  userType,
  onChange = () => { },
  AddImageProps = {},
  EditImageProps = {},
}: FroalaProps) => {
  const model = { id: modelId };
  const useEquationInsert = EQUATION_MODEL_TYPES.includes(modelType);

  const addVideoModalManager = useModalManager();
  const addImageModalManager = useModalManager();
  const editImageModalManager = useModalManager();
  const equationModalManager = useModalManager();

  const [editorInstance, setEditorInstance] = useState<typeof FroalaJsEditor>(null);
  const [currentImageModelId, setCurrentImageModelId] = useState<number>();
  const [isEquation, setIsEquation] = useState(false);

  useScript('https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js', {}, () => {
    window.MathJax = {
      loader: {
        load: ['input/asciimath', 'output/svg'],
      },
    };
  });

  const setEditor = (newEditor: typeof FroalaJsEditor) => {
    setEditorInstance(newEditor);
    if (setEditorCallback) setEditorCallback(newEditor);
  };

  const froalaConfig = TextEditorConfig({
    activePlugins,
    addImageModalManager,
    addVideoModalManager,
    customConfig,
    customEvents,
    editImageModalManager,
    editorClass: className,
    equationModalManager,
    eventHandlers,
    pluginProps,
    setCurrentImageModelId,
    setEditorInstance: setEditor,
    setIsEquation,
    useEquationCommand: useEquationInsert,
    useImageCommand: !!modelId,
    useVideoCommand: !!modelId,
    userType,
  });

  const addImageSubmitHandler = (imageModel: GraphQlImageModel) => {
    if (AddImageProps.afterSubmit) {
      AddImageProps.afterSubmit(imageModel, editorInstance);
    } else {
      insertIntoEditor(editorInstance, graphQlImageModelToTag(imageModel), true, false, true);
    }
  };

  const afterMergeDuplicatesHandler = (imageModels: GraphQlImageModel[]) => {
    if (AddImageProps.afterMergeDuplicates) {
      AddImageProps.afterMergeDuplicates(imageModels, editorInstance);
    }
  };

  const dontMergeCallbackHandler = (imageModel: GraphQlImageModel) => {
    if (AddImageProps.dontMergeCallback) {
      AddImageProps.dontMergeCallback(imageModel, editorInstance);
    }
  };

  return (
    <ApolloProvider client={apolloClient}>
      <QueryClientProvider client={queryClient}>
        <FroalaEditor
          className={className}
          customConfig={froalaConfig}
          htmlName={name}
          model={text}
          text={text}
          onModelChange={onChange}
        />
        <AddImage
          forFroala
          isAdmin
          excludeFields={['display_size', 'zoom_type', 'alignment']}
          modalManager={addImageModalManager}
          modelId={model.id}
          modelType={modelType}
          standaloneButton={false}
          updateResources={() => { }}
          {...AddImageProps}
          afterMergeDuplicates={afterMergeDuplicatesHandler}
          afterSubmit={addImageSubmitHandler}
          dontMergeCallback={dontMergeCallbackHandler}
        />
        {(currentImageModelId || isEquation) && (
          <EditImageModal
            currentImageModelId={currentImageModelId}
            editImageModalManager={editImageModalManager}
            editorInstance={editorInstance}
            isEquation={isEquation}
            modelId={model.id}
            modelType={modelType}
            {...EditImageProps}
          />
        )}
        <ImageMarginEditor editorInstance={editorInstance} />
        {addVideoModalManager.isOpen && (
          <CustomVideoModal
            editorInstance={editorInstance}
            modalManager={addVideoModalManager}
            model={model}
            modelType={modelType}
          />
        )}
        {useEquationInsert && (
          <EquationInsert
            editorInstance={editorInstance}
            modalManager={equationModalManager}
          />
        )}
      </QueryClientProvider>
    </ApolloProvider>
  );
};

export default Froala;
